import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import TextButton from "../../components/primitives/TextButton";
import TextField from "../../components/primitives/TextField";
import { ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

const looksLikeEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s ?? "").trim());

function VerifyPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { verify, resendVerification } = useAuth();
    const token = params.get("token");

    const [status, setStatus] = useState(token ? "verifying" : "missing");
    const [serverErr, setServerErr] = useState(null);
    const [resendEmail, setResendEmail] = useState("");
    const [resendBusy, setResendBusy] = useState(false);
    const [resendSent, setResendSent] = useState(false);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                await verify(token);
                if (!cancelled) {
                    setStatus("ok");
                    setTimeout(
                        () => !cancelled && navigate("/dashboard", { replace: true }),
                        900
                    );
                }
            } catch (err) {
                if (cancelled) return;
                setStatus("invalid");
                setServerErr(
                    err instanceof ApiError && err.code === "invalid_token"
                        ? "This verification link is invalid, expired, or already used."
                        : err?.message || "Verification failed."
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, verify, navigate]);

    const requestResend = async (e) => {
        e?.preventDefault?.();
        if (!looksLikeEmail(resendEmail)) return;
        setResendBusy(true);
        try {
            await resendVerification(resendEmail.trim());
            setResendSent(true);
        } catch {
            setResendSent(true);
        } finally {
            setResendBusy(false);
        }
    };

    if (status === "verifying") {
        return (
            <AuthShell
                eyebrow="Console / Verify email"
                title="Activating your account…"
                lede="One moment."
            >
                <div className="auth-check">
                    <div className="auth-check__icon auth-check__icon--spin" aria-hidden="true">
                        <span className="auth-spinner" />
                    </div>
                </div>
            </AuthShell>
        );
    }

    if (status === "ok") {
        return (
            <AuthShell
                eyebrow="Console / Verify email"
                title="You're in."
                lede="Account activated. Redirecting to your dashboard."
            >
                <div className="auth-check auth-check--success">
                    <div className="auth-check__icon auth-check__icon--success" aria-hidden="true">
                        <CheckCircle2 size={20} />
                    </div>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            eyebrow="Console / Verify email"
            title="Link invalid or expired."
            lede={serverErr || "This link can't be used. Request a new one below."}
            footer={
                <>
                    <Link to="/login" className="auth-shell-link auth-shell-link--accent">
                        ← Back to sign-in
                    </Link>
                </>
            }
        >
            <div className="auth-check auth-check--error">
                <div className="auth-check__icon auth-check__icon--error" aria-hidden="true">
                    <AlertTriangle size={20} />
                </div>
            </div>

            <form className="auth-form" onSubmit={requestResend} noValidate>
                <TextField
                    type="email"
                    label="Email"
                    required
                    value={resendEmail}
                    onChange={setResendEmail}
                    placeholder="you@company.com"
                    autoComplete="email"
                    hint="We'll send a fresh verification link."
                />
                <TextButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={resendBusy}
                    disabled={resendSent}
                >
                    {resendSent ? "Sent — check your inbox" : "Send a new link"}
                </TextButton>
            </form>
        </AuthShell>
    );
}

export default VerifyPage;
